import React, { useState, useEffect } from 'react';
import LoadError from '../../../components/admin/LoadError';
import RowActions from '../../../components/admin/RowActions';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  MenuItem,
  InputAdornment,
  Chip,
  Avatar,
  Button,
  Card,
  CardContent,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Switch,
} from '@mui/material';
import {
  Search as SearchIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  PersonAdd as PersonAddIcon,

  Email as EmailIcon,

  Payments as PaymentsIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import api from '../../../api';
import { toast } from 'react-toastify';
import ResponsiveTable from '../../../components/ResponsiveTable';
import { adminHasPermission } from '../../../utils/adminPermissions';
import StudentPurchaseResourcesDialog from './StudentPurchaseResourcesDialog';
import { AccessBreakdown, MobilePlanChip } from './StudentAccessBits';
import LastSeenInfo from '../../../components/LastSeenInfo';

const StudentList = () => {
  const navigate = useNavigate();
  const canUpdateStudents = adminHasPermission('update-students');
  const canDeleteStudents = adminHasPermission('delete-students');
  const canCreateStudents = adminHasPermission('create-students');
  
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [totalRecords, setTotalRecords] = useState(0);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, student: null });
  const [purchaseDialog, setPurchaseDialog] = useState({ open: false, student: null });
  const [purchasedType, setPurchasedType] = useState('');
  // Course, group and mobile-plan filters (admin/students/filter-options).
  const [courseFilter, setCourseFilter] = useState('');
  const [planFilter, setPlanFilter] = useState('');
  const [mobileFilter, setMobileFilter] = useState('');
  const [bothFilter, setBothFilter] = useState(false);
  const [filterOptions, setFilterOptions] = useState({ courses: [], plans: [] });

  useEffect(() => {
    api.get('admin/students/filter-options')
      .then((r) => setFilterOptions(r.data?.data || { courses: [], plans: [] }))
      .catch(() => {});
  }, []);

  useEffect(() => {
    fetchStudents();
  }, [page, rowsPerPage, searchTerm, purchasedType, courseFilter, planFilter, mobileFilter, bothFilter]);

  const fetchStudents = async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const response = await api.get('admin/students-with-courses', {
        params: {
          page: page + 1,
          per_page: rowsPerPage,
          search: searchTerm,
          ...(purchasedType ? { purchased_type: purchasedType } : {}),
          ...(courseFilter ? { course_id: courseFilter } : {}),
          ...(planFilter ? { course_plan_id: planFilter } : {}),
          ...(mobileFilter ? { mobile_status: mobileFilter } : {}),
          ...(bothFilter ? { course_and_plan: 1 } : {}),
        }
      });
      
      if (response.data.success) {
        const data = response.data.data;
        setStudents(data.data || []);
        setTotalRecords(data.total || 0);
      }
    } catch (error) {
      setLoadError(error || true);
      console.error('Error fetching students:', error);
      toast.error('Failed to fetch student list');
    } finally {
      setLoading(false);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleDeleteClick = (student) => {
    setDeleteDialog({ open: true, student });
  };

  const handleDeleteConfirm = async () => {
    const { student } = deleteDialog;
    try {
      const response = await api.delete(`admin/student/${student.id}`);
      if (response.data.success) {
        toast.success('Student deleted successfully');
        fetchStudents(); // Refresh the list
      }
    } catch (error) {
      console.error('Error deleting student:', error);
      if (error.response?.status === 400) {
        toast.error('Cannot delete student because they are assigned to courses');
      } else {
        toast.error('Failed to delete student');
      }
    } finally {
      setDeleteDialog({ open: false, student: null });
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialog({ open: false, student: null });
  };

  return (
    <Box sx={{ 
      p: 3, 
      minHeight: 0,
      background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)'
    }}>
      <Box sx={{ mb: 4, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
        <Box>
        <Typography component="h1" sx={{ fontSize: { xs: 20, sm: 24 }, fontWeight: 700, color: '#111827', lineHeight: 1.3, mb: 2 }}>
          Student Management
        </Typography>
        <Typography variant="body1" sx={{ color: '#5a6c7d', ml: 1 }}>
          Manage student accounts, view assigned courses, and handle student data
        </Typography>
        </Box>
        {canCreateStudents && (
          <Button
            variant="contained"
            startIcon={<PersonAddIcon />}
            onClick={() => navigate('/admin/student/add')}
            sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', fontWeight: 600, textTransform: 'none', borderRadius: 2 }}
          >
            Add Student
          </Button>
        )}
      </Box>

      <Card sx={{ 
        borderRadius: 4, 
        boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
        background: 'rgba(255,255,255,0.95)',
        backdropFilter: 'blur(20px)'
      }}>
        <CardContent sx={{ p: 4, display: 'flex', flexDirection: 'column', minHeight: 0, maxHeight: 'calc(100dvh - 200px)', overflow: 'hidden' }}>
          <Box sx={{ mb: 4, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'flex-start', flexWrap: 'wrap', gap: 2 }}>
            <TextField
              placeholder="Search students..."
              variant="outlined"
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{ 
                width: 350,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 3,
                  backgroundColor: 'white',
                  '&:hover fieldset': {
                    borderColor: '#667eea',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#667eea',
                  }
                }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              select
              size="small"
              label="Purchased"
              value={purchasedType}
              onChange={(e) => { setPurchasedType(e.target.value); setPage(0); }}
              sx={{ width: 220, '& .MuiOutlinedInput-root': { borderRadius: 3, backgroundColor: 'white' } }}
            >
              <MenuItem value="">Any product</MenuItem>
              <MenuItem value="course">Courses</MenuItem>
              <MenuItem value="mock_exam">Mock exams</MenuItem>
              <MenuItem value="paper">Past papers</MenuItem>
            </TextField>
            <TextField
              select
              size="small"
              label="Course"
              value={courseFilter}
              onChange={(e) => { setCourseFilter(e.target.value); setPlanFilter(''); setPage(0); }}
              sx={{ width: 200, '& .MuiOutlinedInput-root': { borderRadius: 3, backgroundColor: 'white' } }}
            >
              <MenuItem value="">Any course</MenuItem>
              {filterOptions.courses.map((c) => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
            </TextField>
            <TextField
              select
              size="small"
              label="Group"
              value={planFilter}
              onChange={(e) => { setPlanFilter(e.target.value); setPage(0); }}
              sx={{ width: 200, '& .MuiOutlinedInput-root': { borderRadius: 3, backgroundColor: 'white' } }}
            >
              <MenuItem value="">Any group</MenuItem>
              {filterOptions.plans
                .filter((p) => !courseFilter || String(p.course_id) === String(courseFilter))
                .map((p) => <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>)}
            </TextField>
            <TextField
              select
              size="small"
              label="Mobile plan"
              value={mobileFilter}
              onChange={(e) => { setMobileFilter(e.target.value); setPage(0); }}
              sx={{ width: 170, '& .MuiOutlinedInput-root': { borderRadius: 3, backgroundColor: 'white' } }}
            >
              <MenuItem value="">Any</MenuItem>
              <MenuItem value="active">Active (paid)</MenuItem>
              <MenuItem value="trial">Trial</MenuItem>
              <MenuItem value="expired">Ended</MenuItem>
              <MenuItem value="none">No plan</MenuItem>
            </TextField>
            <FormControlLabel
              control={<Switch size="small" checked={bothFilter} onChange={(e) => { setBothFilter(e.target.checked); setPage(0); }} />}
              label="Course + mobile plan"
            />
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden' }}>
          {loadError && !loading && <LoadError what="students" error={loadError} onRetry={() => fetchStudents()} />}
          <ResponsiveTable
            component={Paper}
            elevation={0}
            sx={{
              borderRadius: 3,
              border: '1px solid rgba(0,0,0,0.05)',
              flex: 1,
              minHeight: 0,
              scrollbarGutter: 'stable',
            }}
          >
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, py: 2, background: 'linear-gradient(135deg, #f8f9ff 0%, #e8f4fd 100%)' }}>Student</TableCell>
                  <TableCell sx={{ fontWeight: 600, py: 2, background: 'linear-gradient(135deg, #f8f9ff 0%, #e8f4fd 100%)' }}>Parent Details</TableCell>
                  <TableCell sx={{ fontWeight: 600, py: 2, background: 'linear-gradient(135deg, #f8f9ff 0%, #e8f4fd 100%)' }}>Display Name</TableCell>
                  <TableCell sx={{ fontWeight: 600, py: 2, background: 'linear-gradient(135deg, #f8f9ff 0%, #e8f4fd 100%)' }}>School year</TableCell>
                  <TableCell sx={{ fontWeight: 600, py: 2, background: 'linear-gradient(135deg, #f8f9ff 0%, #e8f4fd 100%)' }}>Academic year</TableCell>
                  <TableCell sx={{ fontWeight: 600, py: 2, background: 'linear-gradient(135deg, #f8f9ff 0%, #e8f4fd 100%)' }}>Course &amp; group</TableCell>
                  <TableCell sx={{ fontWeight: 600, py: 2, background: 'linear-gradient(135deg, #f8f9ff 0%, #e8f4fd 100%)' }}>Mobile plan</TableCell>
                  <TableCell sx={{ fontWeight: 600, py: 2, background: 'linear-gradient(135deg, #f8f9ff 0%, #e8f4fd 100%)' }}>Purchased</TableCell>
                  <TableCell sx={{ fontWeight: 600, py: 2, background: 'linear-gradient(135deg, #f8f9ff 0%, #e8f4fd 100%)' }}>Past papers</TableCell>
                                    <TableCell sx={{ fontWeight: 600, py: 2, background: 'linear-gradient(135deg, #f8f9ff 0%, #e8f4fd 100%)' }}>Mock exams</TableCell>
                  <TableCell sx={{ fontWeight: 600, py: 2, background: 'linear-gradient(135deg, #f8f9ff 0%, #e8f4fd 100%)' }}>Last seen</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, py: 2 }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={12} align="center" sx={{ py: 8 }}>
                      <Typography color="text.secondary">Loading students...</Typography>
                    </TableCell>
                  </TableRow>
                ) : students.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={12} align="center" sx={{ py: 8 }}>
                      <Typography color="text.secondary">No students found</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  students.map((student) => (
                    <TableRow 
                      key={student.id} 
                      hover
                      sx={{ '&:hover': { bgcolor: 'rgba(102, 126, 234, 0.04)' } }}
                    >
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Avatar sx={{ 
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', 
                            width: 40, 
                            height: 40,
                            fontSize: '1rem',
                            fontWeight: 600
                          }}>
                            {student.student?.first_name?.[0] || 'S'}
                          </Avatar>
                          <Box>
                            <Typography variant="body2" fontWeight={600} color="#2d3748">
                              {student.student?.first_name} {student.student?.last_name}
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                              <EmailIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                              <Typography variant="caption" color="text.secondary">
                                {student.student?.email}
                              </Typography>
                            </Box>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        {student.parent ? (
                          <Box>
                            <Typography variant="body2" fontWeight={600} color="#2d3748">
                              {student.parent.full_name}
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                              <EmailIcon sx={{ fontSize: 12, color: 'text.secondary' }} />
                              <Typography variant="caption" color="text.secondary">
                                {student.parent.email}
                              </Typography>
                            </Box>
                          </Box>
                        ) : (
                          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                            No parent assigned
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={500} sx={{ color: '#4a5568' }}>
                          {student.display_name || 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={student.school_year?.name || '—'}
                          size="small"
                          sx={{
                            bgcolor: 'rgba(102, 126, 234, 0.1)',
                            color: '#667eea',
                            fontWeight: 600,
                            borderRadius: '6px'
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ color: '#4a5568', fontWeight: 500 }}>
                          {student.academic_year || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ minWidth: 180 }}>
                        {(student.assigned_courses || []).length === 0 ? (
                          <Typography variant="body2" color="text.secondary">—</Typography>
                        ) : (
                          (student.assigned_courses || []).map((c) => (
                            <Box key={`${c.id}-${c.group?.id ?? 0}`} sx={{ mb: 0.5 }}>
                              <Typography variant="body2" fontWeight={600} color="#2d3748">
                                {c.course?.name || `Course #${c.course_id}`}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {c.group?.name || 'No group'}{c.via_parent ? ' · bought by parent' : ''}
                              </Typography>
                            </Box>
                          ))
                        )}
                      </TableCell>
                      <TableCell>
                        <MobilePlanChip plan={student.mobile_plan} />
                      </TableCell>
                      <TableCell sx={{ maxWidth: 260 }}>
                        {student.purchased_summary ? (
                          <Tooltip
                            title={(student.purchased_products || [])
                              .slice(0, 12)
                              .map((p) => `${p.resource_type_label}: ${p.resource_name}`)
                              .join('\n')}
                          >
                            <Typography variant="body2" sx={{ color: '#4a5568', fontWeight: 500 }}>
                              {student.purchased_summary}
                            </Typography>
                          </Tooltip>
                        ) : (
                          <Typography variant="body2" color="text.secondary">Nothing bought</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <AccessBreakdown data={student.past_papers} />
                      </TableCell>
                      <TableCell>
                        <AccessBreakdown data={student.mock_exams} />
                      </TableCell>
                      <TableCell>
                        <LastSeenInfo user={student.student} />
                      </TableCell>
                      <TableCell align="right">
                        <RowActions
                          actions={[
                            { label: 'View details', icon: <ViewIcon fontSize="small" />, onClick: () => navigate(`/admin/student/${student.id}`) },
                            { label: 'Edit student', icon: <EditIcon fontSize="small" />, onClick: () => navigate(`/admin/student/${student.id}/edit`), show: canUpdateStudents },
                            { label: 'Purchase course / mock / paper', icon: <PaymentsIcon />, onClick: () => setPurchaseDialog({ open: true, student }), show: canUpdateStudents },
                            { label: 'Delete student', icon: <DeleteIcon />, onClick: () => handleDeleteClick(student), show: canDeleteStudents, danger: true },
                          ]}
                        />
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </ResponsiveTable>

          <TablePagination
            component="div"
            count={totalRecords}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{ borderTop: '1px solid rgba(0,0,0,0.05)', flexShrink: 0 }}
          />
          </Box>
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <StudentPurchaseResourcesDialog
        open={purchaseDialog.open}
        student={purchaseDialog.student}
        onClose={(didPurchase) => {
          setPurchaseDialog({ open: false, student: null });
          if (didPurchase) fetchStudents();
        }}
      />

      <Dialog open={deleteDialog.open} onClose={handleDeleteCancel}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the student "{deleteDialog.student?.display_name || 'Unknown'}"? 
            This action cannot be undone.
          </Typography>
          {deleteDialog.student?.assigned_courses_count > 0 && (
            <Typography color="warning.main" sx={{ mt: 2 }}>
              Warning: This student has {deleteDialog.student.assigned_courses_count} assigned courses. 
              Deletion may be blocked if courses are still active.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default StudentList;
